import { createSlice } from "@reduxjs/toolkit";
import axios from "utils/axios";
import rawAxios from "axios";
import { dispatch } from "../index";
import { storage } from "../../utils/helpers/storage";
import { jwtDecode } from "jwt-decode";

// In-memory request deduplication
let pendingFeedbackRequests = {};

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function getCacheKey(dashboardId, siteId) {
  return `${dashboardId}_${siteId}`;
}

function isCacheEntryValid(entry) {
  if (!entry || !entry.fetchedAt) return false;
  return Date.now() - entry.fetchedAt < CACHE_TTL_MS;
}

function getAuthHeaders() {
  const decoded = storage.getAccessToken()
    ? jwtDecode(storage.getAccessToken())
    : null;
  return {
    "locus-domain": decoded?.domain || storage.getDomain(),
    "locus-region": storage.getUserRegion() || storage.getRegion(),
  };
}

// ----------------------------------------------------------------------

export const initialState = {
  error: null,
  // Keyed by `${dashboardId}_${siteId}`
  latestFeedback: {},
  // Keyed by `${dashboardId}_${siteId}` — status per dashboard
  submitStatusByKey: {},
  submitErrorByKey: {},
  // Coordination flag: when header button is clicked, this increments so FeedbackWrapper can react
  modalRequestCounter: 0,
};

const slice = createSlice({
  name: "feedback",
  initialState,
  reducers: {
    hasError(state, action) {
      state.error = action.payload;
    },
    setLatestFeedback(state, action) {
      const { key, data, fetchedAt } = action.payload;
      state.latestFeedback[key] = { data, fetchedAt, loading: false };
    },
    setFeedbackLoading(state, action) {
      const { key } = action.payload;
      if (!state.latestFeedback[key]) {
        state.latestFeedback[key] = { data: null, fetchedAt: null, loading: true };
      } else {
        state.latestFeedback[key].loading = true;
      }
    },
    setFeedbackNotFound(state, action) {
      const { key, fetchedAt } = action.payload;
      state.latestFeedback[key] = { data: null, fetchedAt, loading: false };
    },
    setSubmitStatus(state, action) {
      const { key, status } = action.payload;
      state.submitStatusByKey[key] = status;
    },
    setSubmitError(state, action) {
      const { key, error } = action.payload;
      state.submitErrorByKey[key] = error;
    },
    clearFeedbackCache(state) {
      state.latestFeedback = {};
    },
    clearSubmitState(state, action) {
      const { key } = action.payload;
      if (key) {
        state.submitStatusByKey[key] = "idle";
        state.submitErrorByKey[key] = null;
      }
    },
    requestFeedbackModal(state) {
      state.modalRequestCounter += 1;
    },
  },
});

export default slice.reducer;

export const { clearFeedbackCache, clearSubmitState, requestFeedbackModal } = slice.actions;

// ----------------------------------------------------------------------

/**
 * Get the latest feedback entry for the current user by dashboard and site.
 * Uses in-memory caching and request deduplication.
 */
export function getLatestFeedback({ dashboardId, siteId }) {
  return async () => {
    const key = getCacheKey(dashboardId, siteId);

    // Deduplicate in-flight requests
    if (pendingFeedbackRequests[key]) {
      return pendingFeedbackRequests[key];
    }

    dispatch(slice.actions.setFeedbackLoading({ key }));

    pendingFeedbackRequests[key] = (async () => {
      try {
        // Use rawAxios to bypass the app's response interceptor which strips error.response.status
        const response = await rawAxios.get(
          `${process.env.REACT_APP_API}/analytics/analytics/feedback/${dashboardId}`,
          {
            params: { site: siteId },
            headers: {
              ...getAuthHeaders(),
              Authorization: `Bearer ${storage.getAccessToken()}`,
            },
            validateStatus: (status) => status === 200 || status === 404,
          },
        );

        if (response.status === 404) {
          dispatch(slice.actions.setFeedbackNotFound({ key, fetchedAt: Date.now() }));
          return null;
        }

        dispatch(
          slice.actions.setLatestFeedback({
            key,
            data: response?.data,
            fetchedAt: Date.now(),
          }),
        );
        return response?.data;
      } catch (error) {
        dispatch(slice.actions.hasError(error));
        return null;
      } finally {
        delete pendingFeedbackRequests[key];
      }
    })();

    return pendingFeedbackRequests[key];
  };
}

/**
 * Check if we already have valid cached feedback for this dashboard+site.
 */
export function selectFeedbackCacheValid(state, dashboardId, siteId) {
  const key = getCacheKey(dashboardId, siteId);
  const entry = state.feedback?.latestFeedback?.[key];
  return isCacheEntryValid(entry);
}

/**
 * Select the latest feedback entry from state.
 */
export function selectLatestFeedback(state, dashboardId, siteId) {
  const key = getCacheKey(dashboardId, siteId);
  return state.feedback?.latestFeedback?.[key] || null;
}

/**
 * Select the submit status for a specific dashboard+site.
 */
export function selectSubmitStatus(state, dashboardId, siteId) {
  const key = getCacheKey(dashboardId, siteId);
  return state.feedback?.submitStatusByKey?.[key] || "idle";
}

/**
 * Select the submit error for a specific dashboard+site.
 */
export function selectSubmitError(state, dashboardId, siteId) {
  const key = getCacheKey(dashboardId, siteId);
  return state.feedback?.submitErrorByKey?.[key] || null;
}

/**
 * Submit feedback (thumbs up/down with optional comment).
 */
export function submitFeedback({ dashboardId, siteId, pageName, ratingThumbsUp, comment }) {
  return async () => {
    const key = getCacheKey(dashboardId, siteId);
    dispatch(slice.actions.setSubmitStatus({ key, status: "loading" }));
    dispatch(slice.actions.setSubmitError({ key, error: null }));

    try {
      const body = {
        pageName,
        ratingThumbsUp,
        comment,
        dashboard: dashboardId,
        site: siteId,
        dismissed: false,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API}/analytics/analytics/feedback`,
        body,
        { headers: getAuthHeaders() },
      );

      dispatch(slice.actions.setSubmitStatus({ key, status: "success" }));

      // Update cache with the new entry
      dispatch(
        slice.actions.setLatestFeedback({
          key,
          data: response?.data,
          fetchedAt: Date.now(),
        }),
      );

      return response?.data;
    } catch (error) {
      dispatch(slice.actions.setSubmitStatus({ key, status: "error" }));
      dispatch(
        slice.actions.setSubmitError({
          key,
          error: error?.message || "Failed to submit feedback. Please try again.",
        }),
      );
      return null;
    }
  };
}

/**
 * Dismiss the feedback banner without submitting a rating.
 */
export function dismissFeedback({ dashboardId, siteId, pageName }) {
  return async () => {
    try {
      const body = {
        pageName,
        dashboard: dashboardId,
        site: siteId,
        dismissed: true,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API}/analytics/analytics/feedback`,
        body,
        { headers: getAuthHeaders() },
      );

      // Update cache so banner stays hidden
      const key = getCacheKey(dashboardId, siteId);
      dispatch(
        slice.actions.setLatestFeedback({
          key,
          data: response?.data,
          fetchedAt: Date.now(),
        }),
      );

      return response?.data;
    } catch (error) {
      // Silently fail on dismiss — don't block the user
      console.warn("[Feedback] Dismiss failed:", error);
      return null;
    }
  };
}
